import {
  keepPreviousData,
  QueryClient,
  QueryKey,
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import { QUERY_KEY_ROOTS } from 'variables';
import {
  PageWithLinks,
  type PaginatedResponse,
  Score,
  TeamLearnerResponse,
  TeamResponse,
} from 'api/httpEntities';
import { QueryOptionsConfig, usePaginatedApi } from 'api/hooks';
import {
  CONTAINS,
  DefaultPageParam,
  MINIMUM_SEARCH_CHARACTER_LIMIT_CONTAINS,
  QueryParamsBase,
} from 'api/search';
import usePaginationV2, {
  DEFAULT_TABLE_ITEMS_PER_PAGE,
} from 'components/hooks/usePaginationV2';
import { ProfileType } from 'types/types';
import {
  addAllLearnersToTeam,
  addTeamLearner,
  deleteTeam,
  downloadTeamProfiles,
  getAvailableLearnersForTeam,
  getPaginatedTeams,
  getProfileIds,
  getTeam,
  getTeamColourCountsByOrganisation,
  getTeamLearners,
  getTeamScoreSummary,
  getTeamScoreSummaryByOrganisation,
  getTeamWheelScores,
  removeAllLearnersToTeam,
  removeTeamLearner,
  shareProfiles,
  SummaryResponse,
  TeamPageParams,
} from './teams';
import {
  AvailableLearnersResponse,
  GetTeamLearnersRequest,
  ProfileIds,
  TeamColourCountsByOrganisationResponse,
  TeamLearnerData,
  TeamLearnerPurchaseStatus,
  TeamLearnerSearchTerm,
  WheelPositionSummaryByOrganisationResponse,
} from './teamApiTypes';

export const useGetTeam = (
  teamId: string,
  config: QueryOptionsConfig<TeamResponse> = {},
): UseQueryResult<TeamResponse, Error> =>
  useQuery<TeamResponse, Error>({
    queryKey: [QUERY_KEY_ROOTS.TEAM_DATA, teamId],
    queryFn: () => getTeam(teamId),
    ...config,
  });

export interface PaginatedTeamLearnersResponse extends PageWithLinks {
  response: TeamLearnerResponse[];
}

export interface PaginatedTeamLearnerParams extends DefaultPageParam {
  searchTerm?: TeamLearnerSearchTerm;
  purchaseStatus: TeamLearnerPurchaseStatus;
}

export const useGetPaginatedTeamLearners = (
  teamLearnerRequest: GetTeamLearnersRequest,
  options: {
    enabled: boolean;
    queryKeyScope: TeamLearnersQueryScope;
    keepPreviousResults?: boolean;
  },
) => {
  const {
    teamId,
    searchTerm = '',
    purchaseStatus = 'ALL',
  } = teamLearnerRequest;
  const {
    data,
    isFetching,
    isPlaceholderData,
    status,
    hasNextPage: hasMore,
    fetchNextPage: loadMore,
  } = usePaginatedApi<
    PaginatedTeamLearnersResponse,
    TeamLearnerResponse,
    PaginatedTeamLearnerParams & QueryParamsBase,
    { teamId: string | number }
  >({
    fetchPage: getTeamLearners,
    queryKey: [
      QUERY_KEY_ROOTS.TEAM_LEARNERS_DATA,
      QUERY_KEY_ROOTS.PAGINATED_DATA,
      options.queryKeyScope,
    ],
    getItemsFromPage: (p) => p.response ?? [],
    enabled: options.enabled,
    placeholderData: options.keepPreviousResults ? keepPreviousData : undefined,
    queryParams: {
      searchTerm,
      purchaseStatus,
    },
    pathParams: { teamId },
  });

  const transformTeamLearners = (
    learners: TeamLearnerResponse[],
  ): (TeamLearnerResponse & { learnerId: string })[] =>
    learners.map(({ id, latestEvaluation, latestProfile, ...rest }) => {
      return {
        ...rest,
        latestEvaluation,
        latestProfile,
        id: latestEvaluation?.id ?? id,
        learnerId: id,
      };
    });
  const { items = [], totalCount = 0 } = data ?? {};
  const learners = transformTeamLearners(items);
  const paginator = usePaginationV2(
    learners,
    DEFAULT_TABLE_ITEMS_PER_PAGE,
    totalCount,
  );

  return {
    unprocessedTeamLearners: items,
    isFetching,
    isPlaceholderData,
    status,
    hasMore,
    loadMore,
    totalCount,
    paginator,
  };
};

export const useAddTeamLearner = (
  queryClient: QueryClient,
): UseMutationResult<void, Error, TeamLearnerData, unknown> =>
  useMutation({
    mutationFn: addTeamLearner,
    onSuccess: () => {
      queryClient
        .invalidateQueries({ queryKey: [QUERY_KEY_ROOTS.TEAM_LEARNERS_DATA] })
        .then();
    },
  });

export const useRemoveTeamLearner = (
  queryClient?: QueryClient,
  teamId?: string,
): UseMutationResult<void, Error, TeamLearnerData, unknown> =>
  useMutation({
    mutationFn: removeTeamLearner,
    onSuccess:
      queryClient && teamId
        ? () => {
            queryClient
              .invalidateQueries({
                queryKey: [QUERY_KEY_ROOTS.TEAM_DATA, teamId],
              })
              .then();
            queryClient
              .invalidateQueries({
                queryKey: [QUERY_KEY_ROOTS.TEAM_LEARNERS_DATA],
              })
              .then();
          }
        : undefined,
  });

export const useGetTeamWheelSummary = (
  teamId: string,
  config: QueryOptionsConfig<SummaryResponse> = {},
): UseQueryResult<SummaryResponse, Error> =>
  useQuery({
    queryKey: [QUERY_KEY_ROOTS.TEAM_WHEEL_SUMMARY, teamId] as QueryKey,
    queryFn: async () => {
      return getTeamScoreSummary(teamId);
    },
    ...config,
  });

export const useGetTeamWheelSummaryByOrganisation = (
  teamId: string,
  config: QueryOptionsConfig<WheelPositionSummaryByOrganisationResponse> = {},
): UseQueryResult<WheelPositionSummaryByOrganisationResponse, Error> =>
  useQuery({
    queryKey: [QUERY_KEY_ROOTS.TEAM_WHEEL_SUMMARY, teamId] as QueryKey,
    queryFn: async () => {
      return getTeamScoreSummaryByOrganisation(teamId);
    },
    ...config,
  });

export const useGetTeamColourCountsByOrganisation = (
  teamId: string,
  config: QueryOptionsConfig<TeamColourCountsByOrganisationResponse> = {},
): UseQueryResult<TeamColourCountsByOrganisationResponse, Error> =>
  useQuery({
    queryKey: [QUERY_KEY_ROOTS.TEAM_COLOUR_COUNTS, teamId] as QueryKey,
    queryFn: async () => {
      return getTeamColourCountsByOrganisation(teamId);
    },
    ...config,
  });

export const useGetTeamWheelScores = (
  teamId: string,
  config: QueryOptionsConfig<{ scores: Score[] }> = {},
): UseQueryResult<{ scores: Score[] }, Error> =>
  useQuery({
    queryKey: [QUERY_KEY_ROOTS.TEAM_SCORES_DATA, teamId] as QueryKey,
    queryFn: () => getTeamWheelScores(teamId),
    ...config,
  });

export const useDeleteTeam = (
  queryClient: QueryClient,
): UseMutationResult<void, Error, string, Error> =>
  useMutation({
    mutationFn: deleteTeam,
    onSuccess: () => {
      queryClient
        .invalidateQueries({ queryKey: [QUERY_KEY_ROOTS.TEAMS_DATA] })
        .then();
      queryClient
        .invalidateQueries({ queryKey: [QUERY_KEY_ROOTS.MY_TEAMS_DATA] })
        .then();
      queryClient
        .invalidateQueries({ queryKey: [QUERY_KEY_ROOTS.PAGINATED_TEAMS_DATA] })
        .then();
      queryClient
        .invalidateQueries({
          queryKey: [QUERY_KEY_ROOTS.PAGINATED_MY_TEAMS_DATA],
        })
        .then();
    },
  });

export const useAddAllLearnersToTeam = (
  queryClient: QueryClient,
): UseMutationResult<void, Error, string, Error> =>
  useMutation({
    mutationFn: addAllLearnersToTeam,
    onSuccess: () => {
      queryClient
        .invalidateQueries({ queryKey: [QUERY_KEY_ROOTS.TEAM_DATA] })
        .then();
      queryClient
        .invalidateQueries({ queryKey: [QUERY_KEY_ROOTS.TEAM_LEARNERS_DATA] })
        .then();
    },
  });

export const useRemoveAllLearnersToTeam = (
  queryClient: QueryClient,
): UseMutationResult<void, Error, string, Error> =>
  useMutation({
    mutationFn: removeAllLearnersToTeam,
    onSuccess: () => {
      queryClient
        .invalidateQueries({ queryKey: [QUERY_KEY_ROOTS.TEAM_DATA] })
        .then();
      queryClient
        .invalidateQueries({ queryKey: [QUERY_KEY_ROOTS.TEAM_LEARNERS_DATA] })
        .then();
    },
  });

export const useGetProfileIds = (
  teamId: string,
  profileType: ProfileType,
  learnerIds: string[],
  config = {},
): UseQueryResult<ProfileIds, unknown> =>
  useQuery({
    queryKey: [QUERY_KEY_ROOTS.PROFILES_DATA, profileType, learnerIds, teamId],
    queryFn: async () => getProfileIds(teamId, profileType, { learnerIds }),
    ...config,
  });

export const useShareProfiles = () =>
  useMutation({
    mutationFn: shareProfiles,
  });

export const useDownloadTeamProfiles = () =>
  useMutation({
    mutationFn: downloadTeamProfiles,
  });

export const useGetAvailableLearnersForTeam = (
  teamId: string,
  searchTerm: string,
): UseQueryResult<AvailableLearnersResponse, Error> =>
  useQuery({
    queryKey: [
      QUERY_KEY_ROOTS.TEAM_LEARNERS_DATA,
      'available',
      teamId,
      searchTerm,
    ],
    queryFn: () =>
      getAvailableLearnersForTeam(teamId, { searchTerm, searchMode: CONTAINS }),
    enabled:
      !!teamId && searchTerm.length >= MINIMUM_SEARCH_CHARACTER_LIMIT_CONTAINS,
  });

export const useGetPaginatedTeamsForOrganisation = (
  organisationId: string,
  searchTerm?: string,
) => {
  return usePaginatedApi<
    PaginatedResponse<TeamResponse>,
    TeamResponse,
    TeamPageParams
  >({
    fetchPage: getPaginatedTeams,
    queryKey: [
      QUERY_KEY_ROOTS.PAGINATED_TEAMS_DATA,
      organisationId,
      searchTerm,
    ],
    getItemsFromPage: (p) => p.response ?? [],
    initialPageParam: undefined,
    queryParams: {
      organisationId,
      ...(searchTerm ? { searchMode: CONTAINS, searchTerm } : {}),
    },
    enabled: Boolean(organisationId),
  });
};

export const TEAM_LEARNERS_QUERY_SCOPES = {
  TABLE: 'team-learners-table',
  REMOVE_LEARNER_MODAL: 'remove-learner-modal',
} as const;

export type TeamLearnersQueryScope =
  (typeof TEAM_LEARNERS_QUERY_SCOPES)[keyof typeof TEAM_LEARNERS_QUERY_SCOPES];
